/**
 * auth-session.ts — the broker-app session store (Story ADMIN-W4).
 *
 * D-W4-SESSION-STORE: localStorage, mirroring the accepted portal precedent
 * (portal-session.ts) — a broker works here all day and a tab close must not
 * log them out; server-side expiry/refresh-revocation stays the hard line.
 * Unlike the refresh-less portal store, an EXPIRED access token does NOT clear
 * the session — the refresh token may still mint a fresh one (the api-client's
 * ensureFreshToken/401 seam decides; the admin-session precedent).
 *
 * The snapshot caches DISPLAY-ONLY identity (displayName/email/initials from
 * the ID-token claims — D-W4-DISPLAY-CLAIMS); the api re-verifies the access
 * token on every request (rule #2) and never trusts client-held identity.
 *
 * [Source: apps/web/src/portal/lib/portal-session.ts] (store shape precedent)
 * [Source: apps/web/src/admin/lib/admin-session.ts] (refresh-leg semantics)
 */

import type { OidcTokenSet } from './oidc';
import { decodeIdTokenClaims } from './oidc';

/** localStorage key — distinct from the portal + admin session keys. */
export const BROKER_SESSION_STORAGE_KEY = 'brokly-broker-session';

/** The persisted broker session: the token set + display-only identity. */
export interface BrokerSession {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms after which the access token counts as expired (skew applied). */
  expiresAt: number;
  /** Carried for the Keycloak end-session id_token_hint on logout. */
  idToken: string;
  /** DISPLAY ONLY (sidebar/greeting) — from the ID-token claims. */
  displayName: string | null;
  email: string | null;
  initials: string;
}

/** Derive up-to-2-letter initials from a display name (fallback: email, then '?'). */
export function deriveInitials(displayName: string | null, email: string | null): string {
  const source = displayName?.trim() || '';
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    const initials = `${first}${last}`.toUpperCase();
    if (initials) return initials;
  }
  const emailFirst = email?.trim()[0];
  return emailFirst ? emailFirst.toUpperCase() : '?';
}

/** Build the session snapshot from a token set (initials derived ONCE here). */
export function sessionFromTokenSet(tokens: OidcTokenSet): BrokerSession {
  const claims = decodeIdTokenClaims(tokens.idToken);
  const displayName = claims.name ?? claims.preferredUsername ?? null;
  const email = claims.email ?? null;
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: tokens.expiresAt,
    idToken: tokens.idToken,
    displayName,
    email,
    initials: deriveInitials(displayName, email),
  };
}

// ── Storage (module-local; SSR-safe guards mirror portal-session) ─────────────

let inMemory: BrokerSession | null = null;

function storage(): Storage | null {
  return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
}

/** Persist the session (memory + localStorage). */
export function storeBrokerSession(session: BrokerSession): void {
  inMemory = session;
  const s = storage();
  if (s) s.setItem(BROKER_SESSION_STORAGE_KEY, JSON.stringify(session));
}

/**
 * Read the persisted session (memory first). An expired access token does NOT
 * clear it — the refresh leg may recover (see header).
 */
export function loadBrokerSession(): BrokerSession | null {
  if (inMemory) return inMemory;
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(BROKER_SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as BrokerSession;
    if (!parsed?.accessToken || !parsed?.refreshToken) return null;
    inMemory = parsed;
    return parsed;
  } catch {
    return null;
  }
}

/** Clear the persisted session (logout / failed refresh / hard 401). */
export function clearBrokerSession(): void {
  inMemory = null;
  const s = storage();
  if (s) s.removeItem(BROKER_SESSION_STORAGE_KEY);
}

/** The bearer token for api-client.ts (null when logged out). */
export function getBrokerAccessToken(): string | null {
  return loadBrokerSession()?.accessToken ?? null;
}

/** True when the stored access token is past its (skew-adjusted) expiry. */
export function isBrokerTokenExpired(session: BrokerSession): boolean {
  return Date.now() >= session.expiresAt;
}
